import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Search, Star, MapPin, ArrowRight } from 'lucide-react';
import { clinicService, getLocalizedValue, normalizeImageValue } from '@/services/api';
import { DEFAULT_CLINIC_IMAGE, handleImageFallback } from '@/lib/defaultImages';

const firstArray = (...values) => values.find(Array.isArray) || [];

const extractClinicsPayload = (response) => {
  const payload = response?.data;

  return firstArray(
    payload?.clinics,
    payload?.data?.clinics,
    payload?.data,
    payload
  );
};

const readText = (value, lang) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string' || typeof value === 'number') return String(value).trim();

  const localizedValue = getLocalizedValue(value, lang);
  if (typeof localizedValue === 'string' || typeof localizedValue === 'number') {
    return String(localizedValue).trim();
  }

  return '';
};

const getClinicName = (clinic, lang) =>
  readText(clinic.name, lang) ||
  readText(clinic.display_name, lang) ||
  readText(clinic.name_ar, lang) ||
  readText(clinic.name_en, lang) ||
  (lang === 'ar' ? 'عيادة' : 'Clinic');

const getClinicAddress = (clinic, lang) => {
  const address = clinic.address;

  return (
    readText(address, lang) ||
    readText(address?.address, lang) ||
    readText(address?.description, lang) ||
    readText(clinic.address_text, lang) ||
    readText(clinic.location, lang) ||
    [clinic.city, clinic.area, clinic.street]
      .map((item) => readText(item, lang))
      .filter(Boolean)
      .join(' - ')
  );
};

const getClinicImage = (clinic) =>
  normalizeImageValue(
    clinic.image ||
    clinic.image_url ||
    clinic.logo ||
    clinic.media?.[0]?.url ||
    clinic.media?.[0]?.thumb ||
    clinic.media?.[0]?.icon ||
    ''
  ) || DEFAULT_CLINIC_IMAGE;

const getClinicRating = (clinic) => {
  const rating = Number(clinic.average_rating ?? clinic.rating ?? clinic.rate ?? 0);
  return Number.isFinite(rating) ? rating : 0;
};

const getClinicBookings = (clinic) => {
  const bookings = Number(
    clinic.total_appointments ??
    clinic.appointments_count ??
    clinic.bookings_count ??
    clinic.total_bookings ??
    0
  );

  return Number.isFinite(bookings) ? bookings : 0;
};

const getClinicSpecialties = (clinic, lang) => {
  const rawSpecialties = firstArray(clinic.specialities, clinic.specialties);

  return rawSpecialties
    .map((specialty) => readText(specialty?.name || specialty, lang))
    .filter(Boolean);
};

const ClinicsPage = () => {
  const { i18n } = useTranslation();
  const lang = i18n.language || 'ar';
  const [clinics, setClinics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const fetchClinics = async () => {
      setLoading(true);
      try {
        const response = await clinicService.getAllClinics();
        setClinics(extractClinicsPayload(response));
      } catch (error) {
        console.error('Failed to fetch clinics:', error);
        setClinics([]);
      } finally {
        setLoading(false);
      }
    };

    fetchClinics();
  }, []);

  const normalizedSearchQuery = searchQuery.trim().toLowerCase();
  const filteredClinics = clinics.filter((clinic) => {
    if (!normalizedSearchQuery) return true;

    const searchableText = [
      getClinicName(clinic, lang),
      getClinicAddress(clinic, lang),
      ...getClinicSpecialties(clinic, lang),
    ].join(' ').toLowerCase();

    return searchableText.includes(normalizedSearchQuery);
  });

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900" data-testid="clinics-page">
      <div className="bg-white dark:bg-slate-800 border-b dark:border-slate-700">
        <div className="container mx-auto px-4 py-8">
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-2" data-testid="clinics-title">
            {lang === 'ar' ? 'العيادات' : 'Clinics'}
          </h1>
          <p className="text-slate-600 dark:text-slate-400">
            {lang === 'ar'
              ? 'تصفح جميع العيادات والمراكز الطبية المتاحة'
              : 'Browse all available clinics and medical centers'}
          </p>
        </div>
      </div>

      <div className="bg-white dark:bg-slate-800 border-b dark:border-slate-700 sticky top-16 z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="relative max-w-xl">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder={lang === 'ar' ? 'ابحث عن عيادة أو مركز طبي' : 'Search clinics or medical centers'}
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              className="pl-10"
              data-testid="clinics-search-input"
            />
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-4">
        <p className="text-sm text-slate-500 dark:text-slate-400">
          {lang === 'ar'
            ? `عرض ${filteredClinics.length} من ${clinics.length} عيادة`
            : `Showing ${filteredClinics.length} of ${clinics.length} clinics`}
        </p>
      </div>

      <div className="container mx-auto px-4 pb-8">
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((item) => (
              <Card key={item} className="dark:bg-slate-800 border-0 shadow-sm">
                <CardContent className="p-6">
                  <div className="flex gap-4">
                    <Skeleton className="h-24 w-24 rounded-xl" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-5 w-3/4" />
                      <Skeleton className="h-4 w-1/2" />
                      <Skeleton className="h-4 w-2/3" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : filteredClinics.length === 0 ? (
          <div className="text-center py-12 text-slate-500 dark:text-slate-400" data-testid="no-clinics-message">
            {lang === 'ar' ? 'لا توجد عيادات متاحة حالياً' : 'No clinics available at the moment'}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredClinics.map((clinic, index) => {
              const clinicId = clinic.id ?? clinic.clinic_id;
              const clinicName = getClinicName(clinic, lang);
              const clinicAddress = getClinicAddress(clinic, lang);
              const clinicSpecialties = getClinicSpecialties(clinic, lang);

              return (
                <Card
                  key={clinicId ?? `${clinicName}-${index}`}
                  className="dark:bg-slate-800 border-0 shadow-sm hover:shadow-lg transition-all duration-300"
                  data-testid={`clinic-card-${clinicId ?? index}`}
                >
                  <CardContent className="p-6">
                    <div className="flex gap-4">
                      <div className="h-24 w-24 shrink-0 rounded-xl bg-white flex items-center justify-center overflow-hidden">
                        <img
                          src={getClinicImage(clinic)}
                          alt={clinicName}
                          className="h-full w-full object-contain"
                          loading="lazy"
                          onError={handleImageFallback(DEFAULT_CLINIC_IMAGE)}
                        />
                      </div>

                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-slate-900 dark:text-white mb-2 truncate">
                          {clinicName}
                        </h3>

                        {clinicAddress && (
                          <div className="flex items-center gap-1 text-sm text-slate-600 dark:text-slate-400 mb-2">
                            <MapPin className="h-3.5 w-3.5 shrink-0" />
                            <span className="truncate">{clinicAddress}</span>
                          </div>
                        )}

                        <div className="flex items-center gap-2 text-sm">
                          <div className="flex items-center text-amber-500">
                            <Star className="h-4 w-4 fill-current" />
                            <span className="ml-1 font-medium">{getClinicRating(clinic)}</span>
                          </div>
                          <span className="text-slate-400">
                            ({getClinicBookings(clinic)} {lang === 'ar' ? 'حجز' : 'bookings'})
                          </span>
                        </div>
                      </div>
                    </div>

                    {clinicSpecialties.length > 0 && (
                      <div className="mt-4 flex flex-wrap gap-2">
                        {clinicSpecialties.slice(0, 3).map((specialty) => (
                          <Badge key={specialty} variant="secondary" className="font-normal">
                            {specialty}
                          </Badge>
                        ))}
                      </div>
                    )}

                    <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-700 flex justify-end">
                      <div className="flex gap-2">
                        {clinicId && (
                          <Button size="sm" className="bg-teal-600 hover:bg-teal-700" asChild>
                            <Link to={`/clinics/${encodeURIComponent(clinicId)}`}>
                              {lang === 'ar' ? 'عرض التفاصيل' : 'View Details'}
                              <ArrowRight className="h-4 w-4 ml-2" />
                            </Link>
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ClinicsPage;
